import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Radar as RadarIcon, Target, BellRing, Sparkles, TrendingUp, TrendingDown,
  MapPinned, Search, Star, Phone, Eye, ArrowRight, Building2, Table2, Crown, Settings2,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, PieChart, Pie, Cell,
} from "recharts";

const GREEN = "#22c55e", ORANGE = "#f59e0b", RED = "#ef4444", GOLD = "#F5A623";
const bandHex = (s) => (s >= 70 ? GREEN : s >= 40 ? ORANGE : RED);
const bandKey = (s) => (s >= 70 ? "toContact" : s >= 40 ? "toStudy" : "lowPot");
const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString("fr-FR")} €` : "—");

const card = "rounded-2xl border border-white/10 bg-[#0F1725] p-5 text-white";

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "fr-FR";

  useEffect(() => {
    api.get("/radar/dashboard").then((r) => setD(r.data)).catch(() => setErr(true));
  }, []);

  const bandLabel = (s) => t(`radar.table.${bandKey(s)}`);
  const shortDate = (iso) => { try { return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short" }); } catch { return "—"; } };

  if (err) return <div className="mx-auto max-w-7xl px-6 py-10 text-slate-400" data-testid="dashboard-page">{t("rdash.loadFail")}</div>;
  if (!d) return <div className="mx-auto max-w-7xl px-6 py-10 text-slate-400" data-testid="dashboard-page">{t("rdash.loading")}</div>;

  const empty = (d.counts?.total || 0) === 0;
  const donut = [
    { key: "excellent", value: d.score_distribution.excellent, color: GREEN },
    { key: "good", value: d.score_distribution.good, color: ORANGE },
    { key: "weak", value: d.score_distribution.weak, color: RED },
  ];
  const trend = (d.trend_7d || []).map((p) => ({ d: shortDate(p.date), v: p.count }));

  const Delta = ({ v }) => {
    if (!v) return <span className="text-[11px] text-slate-500">{t("rdash.stable")} · {t("rdash.vsYesterday")}</span>;
    const up = v > 0;
    return <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: up ? GREEN : RED }}>{up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{v} <span className="text-slate-500">{t("rdash.vsYesterday")}</span></span>;
  };

  const stats = [
    { label: t("rdash.today"), value: d.today, delta: <Delta v={d.today_delta} />, icon: Target, color: GOLD },
    { label: t("rdash.toContact"), value: d.counts.green, sub: t("rdash.greenSub"), icon: Sparkles, color: GREEN },
    { label: t("rdash.activeAlerts"), value: d.active_alerts, sub: t("rdash.searchesSub", { n: d.active_searches }), icon: BellRing, color: GOLD },
    { label: t("rdash.week"), value: d.week, sub: t("rdash.avgScore", { n: d.avg_score }), icon: TrendingUp, color: "#3B82F6" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="dashboard-page">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A87C]">RADAR</p>
          <h1 className="mt-1 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("rdash.title")}</h1>
          <p className="mt-2 text-slate-500">{t("rdash.subtitle")}</p>
          <button onClick={() => navigate("/mon-compte#alertes")} data-testid="dash-alerts-counter" title={t("rdash.alertsShortcut")} className="group mt-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0F1725] px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-[#C6A87C]/50 hover:text-white">
            <BellRing className="h-3.5 w-3.5" style={{ color: GOLD }} />
            <span><span className="font-bold text-white">{d.alerts_sent_today ?? 0}</span><span className="text-slate-500">/{d.alerts_max ?? 4}</span> {t("rdash.alertsCounter")}</span>
            <Settings2 className="h-3.5 w-3.5 text-slate-500 transition-colors group-hover:text-[#C6A87C]" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/radar")} data-testid="dash-cta-table" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-[#0F172A] transition-colors hover:border-[#C6A87C]"><Table2 className="h-4 w-4" /> {t("rdash.openTable")}</button>
          <button onClick={() => navigate("/radar")} data-testid="dash-cta-search" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e293b]"><Search className="h-4 w-4" /> {t("rdash.newSearch")}</button>
        </div>
      </header>

      {empty ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="dash-empty">
          <RadarIcon className="mx-auto h-12 w-12 text-[#C6A87C]/50" />
          <p className="mt-4 text-lg font-semibold text-[#0F172A]">{t("rdash.emptyTitle")}</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{t("rdash.emptyDesc")}</p>
          <button onClick={() => navigate("/radar")} data-testid="dash-empty-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C6A87C] px-6 py-3 text-sm font-bold text-[#0F172A] transition-transform hover:scale-[1.03]"><Target className="h-4 w-4" /> {t("rdash.emptyCta")} <ArrowRight className="h-4 w-4" /></button>
        </div>
      ) : (
        <>
          {/* Bandeau — Meilleure opportunité du jour (24 h), 100% réel */}
          {d.best_today ? (
            <div className="mb-4 flex flex-col gap-4 rounded-2xl bg-gradient-to-r from-[#0F1725] to-[#141d2e] p-5 text-white sm:flex-row sm:items-center sm:justify-between" data-testid="dash-best-today" style={{ borderLeft: "4px solid #C6A87C" }}>
              <div className="flex min-w-0 items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white" style={{ background: bandHex(d.best_today.score) }}>{d.best_today.score}</span>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#C6A87C]"><Crown className="h-3 w-3" /> {t("rdash.bestLabel")}</p>
                  <p className="mt-0.5 truncate text-base font-semibold">{d.best_today.property_type || "—"} · {d.best_today.city || "—"}{d.best_today.quartier ? ` · ${d.best_today.quartier}` : ""}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm">
                    <span className="font-semibold" style={{ color: bandHex(d.best_today.score) }}>{bandLabel(d.best_today.score)}</span>
                    <span className="text-slate-300">{eur(d.best_today.current_price)}</span>
                    {d.best_today.margin_estimate != null
                      ? <span className="text-slate-400">{t("rdash.marginEst")} : <span className="font-semibold text-emerald-400">+{eur(d.best_today.margin_estimate)}</span> <span className="text-[10px] italic text-slate-500">({t("rdash.estimation")})</span></span>
                      : <span className="text-[11px] italic text-slate-500">{t("rdash.marginNa")}</span>}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => navigate("/radar")} data-testid="dash-best-view" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-white/15"><Eye className="h-4 w-4" /> {t("rdash.viewAnalysis")}</button>
                {d.best_today.contact_phone && (
                  <a href={`tel:${d.best_today.contact_phone}`} data-testid="dash-best-contact" className="inline-flex items-center gap-1.5 rounded-full bg-[#C6A87C] px-4 py-2.5 text-sm font-bold text-[#0B1220] transition-all hover:brightness-105"><Phone className="h-4 w-4" /> {t("rdash.contact")}</a>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-4 rounded-2xl border border-white/10 bg-[#0F1725] p-4 text-center text-sm text-slate-400" data-testid="dash-best-none">{t("rdash.noneToday")}</div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className={card} data-testid={`dash-stat-${i}`}>
                <div className="flex items-start justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{s.label}</p>
                  <s.icon className="h-4 w-4" style={{ color: s.color }} />
                </div>
                <p className="mt-3 font-display text-4xl tracking-tight">{s.value}</p>
                <div className="mt-1">{s.delta || <span className="text-[11px] text-slate-500">{s.sub}</span>}</div>
              </motion.div>
            ))}
          </div>

          {/* Charts */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
            <div className={card} data-testid="dash-trend">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{t("rdash.trend")}</p>
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                    <defs><linearGradient id="dg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GREEN} stopOpacity={0.45} /><stop offset="100%" stopColor={GREEN} stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="d" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, color: "#fff", fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} />
                    <Area type="monotone" dataKey="v" name={t("rdash.detections")} stroke={GREEN} strokeWidth={2.5} fill="url(#dg)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className={card} data-testid="dash-distribution">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{t("rdash.distribution")}</p>
              <div className="flex items-center gap-5">
                <div style={{ width: 130, height: 130 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={donut} dataKey="value" innerRadius={40} outerRadius={62} paddingAngle={2} stroke="none">{donut.map((x) => <Cell key={x.key} fill={x.color} />)}</Pie></PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 text-sm">
                  {donut.map((x) => (
                    <div key={x.key} className="flex items-center gap-2 text-slate-300"><span className="h-2.5 w-2.5 rounded-full" style={{ background: x.color }} /> {t(`rdash.${x.key}`)} <span className="ml-auto font-bold text-white">{x.value}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline + suivi */}
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {[
              { label: t("radar.table.toContact"), value: d.counts.green, color: GREEN },
              { label: t("radar.table.toStudy"), value: d.counts.orange, color: ORANGE },
              { label: t("radar.table.lowPot"), value: d.counts.red, color: RED },
              { label: t("rdash.contacted"), value: d.tracking.contacted, color: GREEN },
              { label: t("rdash.following"), value: d.tracking.following, color: "#3B82F6" },
              { label: t("rdash.favorites"), value: d.tracking.favorites, color: GOLD },
            ].map((x, i) => (
              <div key={i} className={`${card} !p-4`} data-testid={`dash-pipe-${i}`} style={{ borderLeft: `3px solid ${x.color}` }}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{x.label}</p>
                <p className="mt-1.5 font-display text-3xl" style={{ color: x.color }}>{x.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Top opportunités */}
            <div className={card} data-testid="dash-top">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("rdash.topOpp")}</p>
                <button onClick={() => navigate("/radar")} className="flex items-center gap-1 text-xs font-medium text-[#C6A87C] hover:text-white">{t("rdash.seeAll")} <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
              <div className="space-y-2">
                {d.top_opportunities.map((l) => (
                  <div key={l.id} onClick={() => navigate(`/radar?listing=${l.id}`)} data-testid={`dash-top-${l.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] p-2.5 transition-colors hover:bg-white/[0.05]">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white" style={{ background: bandHex(l.score) }}>{l.score}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">{l.property_type || "—"} · {l.city || "—"}{l.quartier ? ` · ${l.quartier}` : ""}</p>
                      <p className="text-xs" style={{ color: bandHex(l.score) }}>{bandLabel(l.score)}</p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-white">{eur(l.current_price)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Activité récente + secteurs */}
            <div className="space-y-4">
              <div className={card} data-testid="dash-activity">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{t("rdash.recent")}</p>
                <div className="space-y-1.5">
                  {d.recent_activity.length === 0 ? <p className="text-sm text-slate-500">{t("rdash.noActivity")}</p> :
                    d.recent_activity.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: bandHex(l.score) }} />
                        <span className="min-w-0 flex-1 truncate">{t("rdash.detected")} · {l.city || "—"} · <span className="text-slate-400">{l.source_host || t("radar.table.manualSource")}</span></span>
                        <span className="shrink-0 text-xs text-slate-500">{shortDate(l.first_seen)}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`${card} !p-4`} data-testid="dash-sectors">
                  <div className="flex items-center gap-2 text-slate-400"><MapPinned className="h-4 w-4" /> <span className="text-[10px] font-semibold uppercase">{t("rdash.sectors")}</span></div>
                  <p className="mt-1.5 font-display text-3xl">{d.sectors_watched}</p>
                </div>
                <div className={`${card} !p-4`} data-testid="dash-avgpm2">
                  <div className="flex items-center gap-2 text-slate-400"><Building2 className="h-4 w-4" /> <span className="text-[10px] font-semibold uppercase">{t("rdash.avgPm2")}</span></div>
                  <p className="mt-1.5 font-display text-3xl">{d.avg_pm2 ? `${d.avg_pm2.toLocaleString("fr-FR")}` : "—"}<span className="text-sm text-slate-500"> €/m²</span></p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
